// Playable-game registry — PURE / node-safe (no JSX imports, so the settlement
// engines stay unit-testable). Each entry: tile fields, setup options, and a
// pure settle(players, scores, opts, ctx). Game-specific React UI is mapped in
// ggGameUI.jsx. Wolf is the reference implementation.
import { settleWolf } from './ggPlayWolf.js'
import { withScores, pairwise, holeArray } from './ggPlayMoney.js'
import { calcNines } from './ggNines.js'
import { calcChicago } from './ggChicago.js'
import { holeWinners, computeRabbit } from './ggRabbit.js'
import { calcMatchPlay } from './ggMatchPlay.js'
import { calcStroke } from './ggStrokePlay.js'
import { calcDogfight } from './ggDogfight.js'
import { calcVegas } from './ggVegas.js'
import { calcScotch } from './ggScotch.js'
import { calcFourBall } from './ggFourBall.js'
import { dotsFromScores } from './ggDots.js'
import { computeBbb } from './ggBingoBangoBongo.js'
import { computeSnake } from './ggSnake.js'
import { calcSixes } from './ggSixes.js'
import { calcShamble } from './ggShamble.js'
import { calcScramble } from './ggScramble.js'
import { computeSkins, settleSkins } from './ggPlaySkins.js'
import { settleScatsGame } from './ggPlayScats.js'

// Split a 4-player 2-team game into t1/t2 (with scores). team 0 → t1, 1 → t2.
function teams2(players, scores) {
  const ws = withScores(players, scores)
  const t1 = [], t2 = []
  players.forEach((p, i) => { (p.team ? t2 : t1).push(ws[i]) })
  return { t1, t2 }
}
// Zero-sum teamSplit → per-player $ map + sorted rows (Team A/B column).
function teamResult(players, t1, t2, team1Net) {
  const netById = {}
  t1.forEach((p) => { netById[p.id] = team1Net / t1.length })
  t2.forEach((p) => { netById[p.id] = -team1Net / t2.length })
  const rows = players
    .map((p) => ({ id: p.id, name: p.name, team: p.team ? 'B' : 'A', net: netById[p.id] || 0 }))
    .sort((a, b) => b.net - a.net)
  return { rows, nets: netById, columns: [{ key: 'team', label: 'Team' }] }
}

// Shuffle partner rotation by player index — verbatim port of the iOS
// shuffleRotation.ts cycleFor (3–6 players).
function shuffleCycle(n) {
  if (n === 3) return [{ teamA: [0], teamB: [1] }, { teamA: [1], teamB: [2] }, { teamA: [0], teamB: [2] }]
  if (n === 4) return [{ teamA: [0, 1], teamB: [2, 3] }, { teamA: [0, 2], teamB: [1, 3] }, { teamA: [0, 3], teamB: [1, 2] }]
  if (n === 5) return [{ teamA: [0, 1], teamB: [2, 3] }, { teamA: [0, 2], teamB: [3, 4] }, { teamA: [0, 3], teamB: [1, 4] }, { teamA: [1, 2], teamB: [0, 4] }, { teamA: [2, 4], teamB: [1, 3] }]
  if (n === 6) return [[[0, 1, 2], [3, 4, 5]], [[0, 1, 3], [2, 4, 5]], [[0, 1, 4], [2, 3, 5]], [[0, 1, 5], [2, 3, 4]], [[0, 2, 3], [1, 4, 5]], [[0, 2, 4], [1, 3, 5]], [[0, 2, 5], [1, 3, 4]], [[0, 3, 4], [1, 2, 5]], [[0, 3, 5], [1, 2, 4]], [[0, 4, 5], [1, 2, 3]]].map(([a, b]) => ({ teamA: a, teamB: b }))
  return []
}

// Helper: pairwise money from a per-player quantity, aligned to players order.
function pairwiseNets(players, valById, pv) {
  const ids = players.map((p) => p.id)
  const arr = pairwise(ids.map((id) => valById[id] || 0), pv)
  const out = {}; ids.forEach((id, i) => { out[id] = arr[i] })
  return out
}

export const WOLF_VARIANTS = [
  { key: 'standard', label: 'Wolf (standard)' },
  { key: 'biggestLoser', label: 'Biggest Loser' },
  { key: 'blShuck', label: 'Biggest Loser Shuck (6+)' },
]

export const PLAYABLE_GAMES = [
  {
    key: 'wolf',
    label: 'Wolf',
    icon: '🐺',
    description: 'Team up each hole — or go lone wolf',
    minPlayers: 3,
    maxPlayers: 10,
    ordered: true, // player order = the Wolf rotation
    options: [
      { key: 'variant', label: 'Variant', type: 'select', options: ['standard', 'biggestLoser', 'blShuck'], default: 'standard' },
      { key: 'baseBet', label: '$ / hole', type: 'number', default: 2 },
      { key: 'wolfPayout', label: 'Partner payout', type: 'select', options: ['per-winner', 'split-pot'], default: 'per-winner' },
      { key: 'allowBlind', label: 'Allow Blind Wolf (2× stake)', type: 'bool', default: false },
      { key: 'allowHowl', label: 'Allow Howl (2× stake)', type: 'bool', default: false },
      { key: 'strokeMode', label: 'Stroke mode', type: 'select', options: ['lowest', 'cap1', 'full'], default: 'lowest' },
    ],
    // Net comes from the single game-level "Handicaps (net)" toggle (ctx.handicapsOn),
    // not a separate option — one main-game net decision, like the app.
    settle: (players, scores, opts, ctx) => settleWolf(players, scores, ctx?.wolf || {}, {
      baseBet: Number(opts.baseBet) || 0,
      wolfPayout: opts.wolfPayout || 'per-winner',
      isBls: opts.variant === 'blShuck',
      net: !!ctx?.handicapsOn,
      strokeMode: opts.strokeMode || 'lowest',
      strokeIndex: ctx?.strokeIndex,
      overrides: ctx?.wolfBet?.overrides || {},
      multipliers: ctx?.wolfBet?.multipliers || {},
    }),
  },
  {
    key: 'nines',
    label: 'Nines (9-point)',
    icon: '9️⃣',
    description: 'Nine points split among three players each hole',
    minPlayers: 3,
    maxPlayers: 3,
    options: [
      { key: 'pointValue', label: '$ / point', type: 'number', default: 1 },
    ],
    // Points from calcNines (gross, like the app), money via zero-sum pairwise —
    // identical to the iOS settleGame('nines') path.
    settle: (players, scores, opts) => {
      const r = calcNines(withScores(players, scores))
      const totById = {}; r.forEach((x) => { totById[x.id] = x.total })
      const ids = players.map((p) => p.id)
      const nets = pairwise(ids.map((id) => totById[id] || 0), Number(opts.pointValue) || 0)
      const netById = {}; ids.forEach((id, i) => { netById[id] = nets[i] })
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, points: totById[p.id] || 0, net: netById[p.id] || 0 }))
        .sort((a, b) => b.points - a.points)
      return { rows, nets: netById, columns: [{ key: 'points', label: 'Points' }] }
    },
  },
  {
    key: 'chicago',
    label: 'Chicago',
    icon: '🏙️',
    description: 'Quota points — beat your handicap target',
    minPlayers: 2,
    maxPlayers: 8,
    options: [
      { key: 'pointValue', label: '$ / point', type: 'number', default: 1 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Per-point'], labels: { Pot: 'Pot', 'Per-point': 'Per Point' }, default: 'Pot' },
    ],
    // calcChicago → vsQuota; money = pairwise(vsQuota) — iOS settleGame('chicago').
    settle: (players, scores, opts, ctx) => {
      const r = calcChicago(withScores(players, scores), ctx?.pars || Array(18).fill(4))
      const by = {}; r.forEach((x) => { by[x.id] = x })
      const netById = pairwiseNets(players, Object.fromEntries(r.map((x) => [x.id, x.vsQuota])), Number(opts.pointValue) || 0)
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, points: by[p.id]?.total ?? 0, vsQuota: by[p.id]?.vsQuota ?? 0, net: netById[p.id] || 0 }))
        .sort((a, b) => b.vsQuota - a.vsQuota)
      return { rows, nets: netById, columns: [{ key: 'points', label: 'Points' }, { key: 'vsQuota', label: 'vs Quota' }] }
    },
  },
  {
    key: 'rabbit',
    label: 'Rabbit',
    icon: '🐇',
    description: 'Win a hole to grab the rabbit; hold it at the turn/finish',
    minPlayers: 3,
    maxPlayers: 8,
    // App uses ONE combined pot value ($ pot / nine → betPerHole). Mirrored
    // into front/back pots in GGameSetup so the web computeRabbit settle works.
    options: [
      { key: 'betPerHole', label: '$ pot / nine', type: 'number', default: 10 },
    ],
    // holeWinners→computeRabbit; money = pot funded equally, holder takes it
    // (zero-sum potShare) — iOS settleGame('rabbit').
    settle: (players, scores, opts) => {
      const winners = holeWinners(withScores(players, scores))
      const r = computeRabbit(winners, { frontPot: Number(opts.frontPot) || 0, backPot: Number(opts.backPot) || 0 })
      const n = players.length
      const idx = {}; players.forEach((p, i) => { idx[p.id] = i })
      const nets = Array(n).fill(0)
      const potShare = (holder, pot) => {
        if (!holder || pot <= 0 || idx[holder] == null) return
        const wi = idx[holder]
        for (let i = 0; i < n; i++) nets[i] += (i === wi ? pot * (n - 1) / n : -pot / n)
      }
      potShare(r.frontHolder, Number(opts.frontPot) || 0)
      potShare(r.backHolder, Number(opts.backPot) || 0)
      const netById = {}; players.forEach((p, i) => { netById[p.id] = nets[i] })
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, holds: `${r.frontHolder === p.id ? 'Front ' : ''}${r.backHolder === p.id ? 'Back' : ''}`.trim() || '—', net: netById[p.id] }))
        .sort((a, b) => b.net - a.net)
      return { rows, nets: netById, columns: [{ key: 'holds', label: 'Rabbit' }] }
    },
  },
  {
    key: 'matchplay',
    label: 'Match Play',
    icon: '⚔️',
    description: 'Hole-by-hole match, up / down / all-square',
    minPlayers: 2,
    maxPlayers: 2,
    options: [
      { key: 'baseBet', label: '$ / hole', type: 'number', default: 5 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Match'], labels: { Pot: 'Pot', Match: 'Per Hole' }, default: 'Pot' },
    ],
    // p1Up (net holes) × $/hole — matches the app's calcRunningBets(matchPlay).
    settle: (players, scores, opts) => {
      const ps = withScores(players, scores)
      if (ps.length < 2) return { rows: [], nets: {}, columns: [] }
      const r = calcMatchPlay(ps[0], ps[1])
      const bet = Number(opts.baseBet) || 0
      const net = r.p1Up * bet
      const netById = { [players[0].id]: net, [players[1].id]: -net }
      const label = (up) => up > 0 ? `${up} up` : up < 0 ? `${-up} dn` : 'AS'
      const rows = [
        { id: players[0].id, name: players[0].name, match: label(r.p1Up), net },
        { id: players[1].id, name: players[1].name, match: label(-r.p1Up), net: -net },
      ].sort((a, b) => b.net - a.net)
      return { rows, nets: netById, columns: [{ key: 'match', label: 'Match' }] }
    },
  },
  {
    key: 'dogfight',
    label: 'Dogfight',
    icon: '🐕',
    description: 'Net points vs a per-player quota',
    minPlayers: 2,
    maxPlayers: 8,
    options: [
      { key: 'dollarsPerPoint', label: '$ / point', type: 'number', default: 1 },
      { key: 'quota', label: 'Quota', type: 'number', default: 36 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Per-point'], labels: { Pot: 'Pot', 'Per-point': 'Per Point' }, default: 'Pot' },
    ],
    // calcDogfight = the app's exact points formula; money = pairwise(vsQuota) —
    // exactly the iOS dogfight-results settlement (Σ over opponents of diff-diff).
    settle: (players, scores, opts, ctx) => {
      const r = calcDogfight(withScores(players, scores), ctx?.pars || Array(18).fill(4), ctx?.strokeIndex, {
        handicapsOn: !!ctx?.handicapsOn, dollarsPerPoint: Number(opts.dollarsPerPoint) || 0, quota: Number(opts.quota) || 36,
      })
      const by = {}; r.forEach((x) => { by[x.id] = x })
      const netById = pairwiseNets(players, Object.fromEntries(r.map((x) => [x.id, x.vsQuota])), Number(opts.dollarsPerPoint) || 0)
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, points: by[p.id]?.total ?? 0, vsQuota: by[p.id]?.vsQuota ?? 0, net: netById[p.id] || 0 }))
        .sort((a, b) => b.vsQuota - a.vsQuota)
      return { rows, nets: netById, columns: [{ key: 'points', label: 'Points' }, { key: 'vsQuota', label: 'vs Quota' }] }
    },
  },
  {
    key: 'stroke-play',
    label: 'Stroke Play',
    icon: '📋',
    description: 'Straight gross/net stroke totals',
    minPlayers: 1,
    maxPlayers: 8,
    options: [
      { key: 'betPerHole', label: 'Ante $ / player', type: 'number', default: 0 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Hole-by-hole', 'Match'], labels: { Pot: 'Stroke Play', 'Hole-by-hole': 'Per Hole', Match: 'Match Play' }, default: 'Pot' },
    ],
    // Standings only on the web — money settles in the app (Pot/Hole/Match).
    settle: (players, scores) => {
      const r = calcStroke(withScores(players, scores))
      const rows = r.map((x) => ({ id: x.id, name: x.name, total: x.total, net: 0 }))
      return { rows, nets: {}, columns: [{ key: 'total', label: 'Total' }] }
    },
  },
  {
    key: 'vegas',
    label: 'Vegas',
    icon: '🎲',
    description: 'Two teams of two — combine scores into a two-digit number',
    minPlayers: 4,
    maxPlayers: 4,
    ordered: true,
    teams: 2,
    options: [
      { key: 'pointValue', label: '$ / point', type: 'number', default: 1 },
      { key: 'tenCap', label: '10-cap (score 10+ → 0)', type: 'bool', default: false },
    ],
    // calcVegas → team totals; money = (t1−t2)×$ split — iOS settleGame('vegas').
    settle: (players, scores, opts, ctx) => {
      const { t1, t2 } = teams2(players, scores)
      if (t1.length < 2 || t2.length < 2) return { rows: [], nets: {}, columns: [] }
      const r = calcVegas(t1, t2, ctx?.pars || Array(18).fill(4))
      return teamResult(players, t1, t2, (r.t1Total - r.t2Total) * (Number(opts.pointValue) || 0))
    },
  },
  {
    key: 'scotch',
    label: 'Scotch (Bridge)',
    icon: '🥃',
    description: 'Two teams — points game ($/point)',
    minPlayers: 4,
    maxPlayers: 4,
    ordered: true,
    teams: 2,
    options: [
      { key: 'pointValue', label: '$ / point', type: 'number', default: 2 },
      { key: 'scLowTeam', label: 'Low Team Score pts', type: 'select', options: ['Off', '1', '2', '3'], default: '1' },
      { key: 'scLowBall', label: 'Low Ball pts', type: 'select', options: ['Off', '1', '2', '3'], default: '1' },
      { key: 'scHighBall', label: 'High Ball pts (Low-High)', type: 'select', options: ['Off', '1', '2', '3'], default: 'Off' },
      { key: 'scBirdie', label: 'Birdie-or-better pts', type: 'select', options: ['Off', '1', '2', '3'], default: '1' },
      { key: 'scCtp', label: 'Closest-to-Pin pts (par 3s)', type: 'select', options: ['Off', '1', '2', '3'], default: 'Off' },
      { key: 'scBlitz', label: 'Blitz (sweep a hole → double its points)', type: 'bool', default: false },
    ],
    // calcScotch → t1Up holes; money = t1Up×$ split — iOS settleGame('scotch').
    settle: (players, scores, opts) => {
      const { t1, t2 } = teams2(players, scores)
      if (t1.length < 2 || t2.length < 2) return { rows: [], nets: {}, columns: [] }
      const r = calcScotch(t1, t2)
      return teamResult(players, t1, t2, r.t1Up * (Number(opts.baseBet) || 0))
    },
  },
  {
    key: 'four-ball',
    label: 'Four-Ball',
    icon: '⛳',
    description: 'Best-ball of two-player teams — hole-by-hole match',
    minPlayers: 4,
    maxPlayers: 4,
    ordered: true,
    teams: 2,
    options: [
      { key: 'baseBet', label: '$ / hole', type: 'number', default: 5 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Match'], labels: { Pot: 'Pot', Match: 'Per Hole' }, default: 'Pot' },
    ],
    // calcFourBall → team holes-up; money = (t1−t2 holes)×$ split.
    settle: (players, scores, opts) => {
      const { t1, t2 } = teams2(players, scores)
      if (t1.length < 2 || t2.length < 2) return { rows: [], nets: {}, columns: [] }
      const r = calcFourBall(t1, t2)
      return teamResult(players, t1, t2, (r.t1Holes - r.t2Holes) * (Number(opts.baseBet) || 0))
    },
  },
  {
    key: 'dots',
    label: 'Dots (Garbage)',
    icon: '🔵',
    description: 'A dot per birdie-or-better; pairwise dot-diff × $',
    minPlayers: 2,
    maxPlayers: 8,
    options: [{ key: 'dotValue', label: '$ / dot', type: 'number', default: 1 }],
    // Birdie dots auto from scores (matches the iOS shared scorecard — no junk
    // taps collected there); money = pairwise(dots) — iOS settleGame('dots').
    settle: (players, scores, opts, ctx) => {
      const dotsMap = dotsFromScores(withScores(players, scores), ctx?.pars || Array(18).fill(4))
      const netById = pairwiseNets(players, dotsMap, Number(opts.dotValue) || 0)
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, dots: dotsMap[p.id] || 0, net: netById[p.id] || 0 }))
        .sort((a, b) => b.dots - a.dots)
      return { rows, nets: netById, columns: [{ key: 'dots', label: 'Dots' }] }
    },
  },
  {
    key: 'bbb',
    label: 'Bingo Bango Bongo',
    icon: '🔔',
    description: 'Points: first on, closest once on, first in — tap each per hole',
    minPlayers: 2,
    maxPlayers: 8,
    options: [{ key: 'pointValue', label: '$ / point', type: 'number', default: 1 }],
    // Award taps (ctx.bbb) → computeBbb points → pairwise — iOS settleGame('bbb').
    settle: (players, scores, opts, ctx) => {
      const r = computeBbb(players, Object.values(ctx?.bbb || {}))
      const by = {}; r.forEach((x) => { by[x.id] = x.points })
      const netById = pairwiseNets(players, Object.fromEntries(r.map((x) => [x.id, x.points])), Number(opts.pointValue) || 0)
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, points: by[p.id] || 0, net: netById[p.id] || 0 }))
        .sort((a, b) => b.points - a.points)
      return { rows, nets: netById, columns: [{ key: 'points', label: 'Points' }] }
    },
  },
  {
    key: 'snake',
    label: 'Snake',
    icon: '🐍',
    description: 'Last three-putt holds the snake — pays out at the end',
    minPlayers: 2,
    maxPlayers: 8,
    options: [{ key: 'snakeAmount', label: 'Snake $', type: 'number', default: 5 }],
    // Three-putt taps (ctx.snake) → computeSnake; holder pays each — iOS
    // settleGame('snake'). (Your app's shared scorecard doesn't tap three-putts,
    // so Snake pays $0 there; the tap here makes it actually play.)
    settle: (players, scores, opts, ctx) => {
      const tp = []
      for (let h = 1; h <= 18; h++) { const pid = ctx?.snake?.[h]?.playerId; if (pid) tp.push({ hole: h, playerId: pid }) }
      const r = computeSnake(withScores(players, scores), tp, Number(opts.snakeAmount) || 0, { model: 'last' })
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, holds: r.holder === p.id ? '🐍' : '', net: r.payouts[p.id] || 0 }))
        .sort((a, b) => b.net - a.net)
      return { rows, nets: r.payouts, columns: [{ key: 'holds', label: 'Snake' }] }
    },
  },
  {
    key: 'nassau',
    label: 'Nassau',
    icon: '🎡',
    description: 'Front / back / overall matches + auto-presses',
    minPlayers: 2,
    maxPlayers: 8,
    options: [
      { key: 'baseBet', label: '$ / bet', type: 'number', default: 5 },
      { key: 'pressAt', label: 'Auto-press when', type: 'select', options: ['Never', '1 Down', '2 Down', '3 Down'], default: '2 Down' },
    ],
    // Pairwise front/back/overall + auto-presses (trigger 2) × stake — a VERBATIM
    // port of the iOS ggEngines nassauMatchSegment + computeAutoPresses (raw
    // scores), so it matches settleGame('nassau') number-for-number.
    settle: (players, scores, opts) => {
      const stake = Number(opts.baseBet) || 0
      const n = players.length
      const nets = Array(n).fill(0)
      const arr = players.map((p) => Array.from({ length: 18 }, (_, h) => {
        const v = Number(scores?.[h + 1]?.[p.id]); return Number.isFinite(v) && v > 0 ? v : null
      }))
      const seg = (A, B, from, to) => { let up = 0; for (let h = from; h <= to; h++) { const a = A[h], b = B[h]; if (!Number.isFinite(a) || !Number.isFinite(b)) continue; if (a < b) up += 1; else if (a > b) up -= 1 } return { up } }
      const presses = (A, B, t = 2, maxHole = 17) => {
        const opened = []; let w = 0
        for (let h = 0; h <= maxHole; h++) {
          const a = A[h], b = B[h]
          if (Number.isFinite(a) && Number.isFinite(b)) { if (a < b) w += 1; else if (a > b) w -= 1 }
          if (Math.abs(w) >= t && h < maxHole) { opened.push(h + 1); w = 0 }
        }
        return opened.map((s) => seg(A, B, s, maxHole))
      }
      for (let a = 0; a < n; a++) {
        for (let b = a + 1; b < n; b++) {
          const A = arr[a], B = arr[b]
          const bets = [seg(A, B, 0, 8), seg(A, B, 9, 17), seg(A, B, 0, 17), ...presses(A, B, 2, 17)]
          for (const s of bets) { if (s.up > 0) { nets[a] += stake; nets[b] -= stake } else if (s.up < 0) { nets[a] -= stake; nets[b] += stake } }
        }
      }
      const netById = {}; players.forEach((p, i) => { netById[p.id] = nets[i] })
      const rows = players.map((p) => ({ id: p.id, name: p.name, net: netById[p.id] })).sort((a, b) => b.net - a.net)
      return { rows, nets: netById, columns: [] }
    },
  },
  {
    key: 'sixes',
    label: 'Sixes (Round Robin)',
    icon: '6️⃣',
    description: 'Partners rotate every six holes — segment wins',
    minPlayers: 4,
    maxPlayers: 4,
    ordered: true,
    // App Sixes stake is per 6-hole segment (dollarsPerSegment). Carried as
    // that key + betPerHole in GGameSetup; web settle stays wins-only.
    options: [
      { key: 'dollarsPerSegment', label: '$ / segment', type: 'number', default: 0 },
    ],
    // calcSixes = segment wins. Your app has NO money for Sixes (wins only), so
    // this mirrors it: standings by segment wins, no settlement.
    settle: (players, scores) => {
      const r = calcSixes(withScores(players, scores))
      const rows = r.map((x) => ({ id: x.id, name: x.name, wins: x.wins, net: 0 }))
      return { rows, nets: {}, columns: [{ key: 'wins', label: 'Segment wins' }] }
    },
  },
  {
    key: 'shamble',
    label: 'Shamble',
    icon: '🤝',
    description: 'Best drive, then own ball — low team total',
    minPlayers: 4,
    maxPlayers: 4,
    ordered: true,
    teams: 2,
    options: [
      { key: 'betPerHole', label: 'Ante $ / player', type: 'number', default: 0 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Hole-by-hole', 'Match'], labels: { Pot: 'Stroke Play', 'Hole-by-hole': 'Per Hole', Match: 'Match Play' }, default: 'Pot' },
    ],
    // calcShamble = low team total. Your app treats Shamble as a scoring format
    // with NO money, so this mirrors it: team standings, no settlement.
    settle: (players, scores) => {
      const { t1, t2 } = teams2(players, scores)
      const r = calcShamble([t1, t2])
      const totOf = {}; r.forEach((tr) => { tr.team.forEach((p) => { totOf[p.id] = tr.total }) })
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, team: p.team ? 'B' : 'A', total: totOf[p.id] ?? 0, net: 0 }))
        .sort((a, b) => a.total - b.total)
      return { rows, nets: {}, columns: [{ key: 'team', label: 'Team' }, { key: 'total', label: 'Team total' }] }
    },
  },
  {
    key: 'skins',
    label: 'Skins',
    icon: '💰',
    description: 'Low score wins the hole; ties carry over',
    minPlayers: 2,
    maxPlayers: 12,
    options: [
      { key: 'skinValue', label: '$ / skin', type: 'number', default: 5 },
      { key: 'carry', label: 'Carryover on ties', type: 'bool', default: true },
    ],
    // Verbatim engine (ggPlaySkins): low gross per hole wins a skin, ties carry;
    // split-pot money (settleSkins), zero-sum. Matches the app's Skins.
    settle: (players, scores, opts) => {
      const ws = withScores(players, scores)
      const grid = ws.map((p) => Array.from({ length: 18 }, (_, h) => { const v = p.scores[h]; return (typeof v === 'number' && v > 0) ? v : null }))
      const inArr = ws.map(() => true)
      const amount = opts.noBets ? 0 : (Number(opts.skinValue) || 0)
      const res = computeSkins(ws.map((p) => p.name), grid, 19, inArr, opts.carry !== false, false)
      const dollars = settleSkins(res, amount, inArr, 'split-pot')
      const netById = {}; players.forEach((p, i) => { netById[p.id] = dollars[i] })
      const rows = players
        .map((p, i) => ({ id: p.id, name: p.name, skins: res.counts[i], net: dollars[i] }))
        .sort((a, b) => b.skins - a.skins || b.net - a.net)
      return { rows, nets: netById, columns: [{ key: 'skins', label: 'Skins' }] }
    },
  },
  {
    key: 'stableford',
    label: 'Stableford',
    icon: '🎯',
    description: 'Points vs par: eagle 4, birdie 3, par 2, bogey 1',
    minPlayers: 2,
    maxPlayers: 12,
    options: [
      { key: 'pointValue', label: '$ / point', type: 'number', default: 0 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Per-point'], labels: { Pot: 'Pot', 'Per-point': 'Per Point' }, default: 'Pot' },
    ],
    // Points vs par (verbatim from the app's calcStableford); money via zero-sum
    // pairwise on points (high points collect) — like Nines / Chicago.
    settle: (players, scores, opts, ctx) => {
      const ws = withScores(players, scores)
      const pars = ctx?.pars || Array(18).fill(4)
      const pt = (s, par) => { if (s == null) return 0; const d = s - par; if (d <= -2) return 4; if (d === -1) return 3; if (d === 0) return 2; if (d === 1) return 1; return 0 }
      const points = ws.map((p) => p.scores.reduce((sum, s, h) => sum + pt((typeof s === 'number' && s > 0) ? s : null, pars[h] ?? 4), 0))
      const pv = opts.noBets ? 0 : (Number(opts.pointValue) || 0)
      const nets = pairwise(points, pv)
      const netById = {}; players.forEach((p, i) => { netById[p.id] = nets[i] })
      const rows = players
        .map((p, i) => ({ id: p.id, name: p.name, points: points[i], net: nets[i] }))
        .sort((a, b) => b.points - a.points || b.net - a.net)
      return { rows, nets: netById, columns: [{ key: 'points', label: 'Points' }] }
    },
  },
  {
    key: 'scats',
    label: 'Scats',
    icon: '💵',
    description: 'One scat per hole to the low under par; pot splits by scats',
    minPlayers: 2,
    maxPlayers: 12,
    options: [{ key: 'buyIn', label: 'Buy-in $', type: 'number', default: 10 }],
    // Verbatim engine (ggPlayScats.settleScatsGame): one scat/hole to the single
    // under-par winner; pot = buy-in × players, split by scats. Zero-sum.
    settle: (players, scores, opts, ctx) => {
      const ws = withScores(players, scores)
      const pars = ctx?.pars || Array(18).fill(4)
      const scoresByHole = {}
      for (let h = 1; h <= 18; h++) { scoresByHole[h] = {}; ws.forEach((p) => { const v = p.scores[h - 1]; if (typeof v === 'number' && v > 0) scoresByHole[h][p.id] = v }) }
      const buyIn = opts.noBets ? 0 : (Number(opts.buyIn) || 0)
      const r = settleScatsGame(players, scoresByHole, pars, { buyIn, optedIn: players.map((p) => p.id) })
      const netById = {}; r.rows.forEach((row) => { netById[row.id] = row.net })
      const rows = r.rows.map((row) => ({ id: row.id, name: row.name, scats: row.scats, net: row.net }))
      return { rows, nets: netById, columns: [{ key: 'scats', label: 'Scats' }] }
    },
  },
  {
    key: 'scramble',
    label: 'Scramble',
    icon: '⛳',
    description: 'All hit from the best shot — low team total',
    minPlayers: 2,
    maxPlayers: 16,
    teams: 2,
    options: [
      { key: 'betPerHole', label: 'Ante $ / player', type: 'number', default: 0 },
      { key: 'betType', label: 'Bet type', type: 'select', options: ['Pot', 'Hole-by-hole', 'Match'], labels: { Pot: 'Stroke Play', 'Hole-by-hole': 'Per Hole', Match: 'Match Play' }, default: 'Pot' },
    ],
    // calcScramble = low team total (best member score per hole). NO primary
    // money (side bets settle via the shell) — team standings only. Teams are
    // chosen the Team Cup way (named teams + click-assign): each player carries
    // its team INDEX (p.team) + p.teamName; grouped into N teams here.
    settle: (players, scores) => {
      const ws = withScores(players, scores)
      const idxs = [...new Set(ws.map((p) => p.team || 0))].sort((a, b) => a - b)
      const groups = idxs.map((ti) => ws.filter((p) => (p.team || 0) === ti))
      const r = calcScramble(groups)
      const totOf = {}; r.forEach((tr) => { tr.team.forEach((p) => { totOf[p.id] = tr.total }) })
      const nameByIdx = {}; ws.forEach((p) => { if (p.teamName) nameByIdx[p.team || 0] = p.teamName })
      const teamLabel = (ti) => nameByIdx[ti] || `Team ${ti + 1}`
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, team: teamLabel(p.team || 0), total: totOf[p.id] ?? 0, net: 0 }))
        .sort((a, b) => a.total - b.total)
      return { rows, nets: {}, columns: [{ key: 'team', label: 'Team' }, { key: 'total', label: 'Team total' }] }
    },
  },
  {
    key: 'shuffle',
    label: 'Shuffle',
    icon: '🔀',
    description: 'Partners rotate every hole — best-ball, $ per hole',
    minPlayers: 3,
    maxPlayers: 6,
    ordered: true,
    options: [{ key: 'baseBet', label: '$ / hole', type: 'number', default: 2 }],
    // Per-hole best-ball (team low) wins $/hole; partners rotate by cycleFor —
    // verbatim port of the iOS shuffle-results settlement.
    settle: (players, scores, opts) => {
      const dph = Number(opts.baseBet) || 0
      const arr = players.map((p) => holeArray(scores, p.id))
      const cyc = shuffleCycle(players.length)
      const nets = Array(players.length).fill(0)
      if (cyc.length) {
        for (let h = 0; h < 18; h++) {
          const e = cyc[h % cyc.length]
          const netI = (i) => { const g = arr[i][h]; return g == null ? Infinity : g }
          const nA = Math.min(...e.teamA.map(netI)), nB = Math.min(...e.teamB.map(netI))
          if (!isFinite(nA) || !isFinite(nB)) continue
          if (nA < nB) { e.teamA.forEach((i) => { nets[i] += dph }); e.teamB.forEach((i) => { nets[i] -= dph }) }
          else if (nB < nA) { e.teamB.forEach((i) => { nets[i] += dph }); e.teamA.forEach((i) => { nets[i] -= dph }) }
        }
      }
      const netById = {}; players.forEach((p, i) => { netById[p.id] = nets[i] })
      const rows = players.map((p) => ({ id: p.id, name: p.name, net: netById[p.id] })).sort((a, b) => b.net - a.net)
      return { rows, nets: netById, columns: [] }
    },
  },
  {
    key: 'team-cup',
    label: 'Team Cup',
    icon: '🏆',
    description: 'Ryder-style team match (best-ball points) + closest-to-pin',
    minPlayers: 2,
    maxPlayers: 16,
    ordered: true,
    teams: 2,
    // CTH (Closest to Hole) is a check-to-expand side bet in the setup (like
    // Scats) — its pot lands in opts.cthAmount, read by the settle below.
    options: [],
    // Team match points (best net per hole: win 1, halve 0.5 — like the app's
    // official scoring). CTH is a par-3 pool (winner takes the pot; carries when
    // none) — mirrors ryder-cup-results (points are NOT money in your app).
    // Named teams via the setup's click-assign: each player carries its team
    // INDEX (p.team) + p.teamName. Generalized to N teams (same points rule);
    // for 2 teams this is identical to the old t1/t2 computation.
    settle: (players, scores, opts, ctx) => {
      const ws = withScores(players, scores)
      const pars = ctx?.pars || Array(18).fill(4)
      const idxs = [...new Set(ws.map((p) => p.team || 0))].sort((a, b) => a - b)
      const groups = idxs.map((ti) => ws.filter((p) => (p.team || 0) === ti))
      const ptsByIdx = {}; idxs.forEach((ti) => { ptsByIdx[ti] = 0 })
      for (let h = 0; h < 18; h++) {
        const bests = groups.map((g) => Math.min(...g.map((p) => p.scores[h] ?? Infinity)))
        if (bests.some((b) => !isFinite(b))) continue
        const min = Math.min(...bests)
        const winners = idxs.filter((_, i) => bests[i] === min)
        if (winners.length === 1) ptsByIdx[winners[0]] += 1
        else winners.forEach((ti) => { ptsByIdx[ti] += 0.5 })
      }
      // CUSTOM scoring adds a front-nine and back-nine bonus: +1 to the team
      // that won the most holes on that nine (split on a tie), awarded only
      // once the nine is fully scored. OFFICIAL (default) is unchanged. Same
      // rule as the app's ryder-cup Custom scoring.
      if (opts.scoringSystem === 'custom') {
        const nineBonus = (start, end) => {
          const wins = {}; idxs.forEach((ti) => { wins[ti] = 0 })
          for (let h = start; h <= end; h++) {
            const bests = groups.map((g) => Math.min(...g.map((p) => p.scores[h] ?? Infinity)))
            if (bests.some((b) => !isFinite(b))) return
            const min = Math.min(...bests)
            const w = idxs.filter((_, i) => bests[i] === min)
            if (w.length === 1) wins[w[0]] += 1
            else w.forEach((ti) => { wins[ti] += 1 / w.length })
          }
          const max = Math.max(...idxs.map((ti) => wins[ti]))
          const leaders = idxs.filter((ti) => wins[ti] === max)
          if (leaders.length === 1) ptsByIdx[leaders[0]] += 1
          else leaders.forEach((ti) => { ptsByIdx[ti] += 1 / leaders.length })
        }
        nineBonus(0, 8); nineBonus(9, 17)
      }
      // CTH par-3 pool: base + carry, awarded to the tapped player, else carries.
      const cthAmt = Number(opts.cthAmount) || 0
      const cth = ctx?.cth || {}
      const netById = {}; players.forEach((p) => { netById[p.id] = 0 })
      let carry = 0
      for (let h = 0; h < 18; h++) {
        if ((pars[h] ?? 4) !== 3) continue
        const pot = cthAmt + carry
        const sel = cth[h + 1]?.playerId
        if (sel && netById[sel] != null) { netById[sel] += pot; carry = 0 } else { carry = pot }
      }
      const nameByIdx = {}; ws.forEach((p) => { if (p.teamName) nameByIdx[p.team || 0] = p.teamName })
      const teamLabel = (ti) => nameByIdx[ti] || `Team ${ti + 1}`
      const rows = players
        .map((p) => ({ id: p.id, name: p.name, team: teamLabel(p.team || 0), teamPts: ptsByIdx[p.team || 0] || 0, net: netById[p.id] }))
        .sort((a, b) => b.teamPts - a.teamPts || b.net - a.net)
      return { rows, nets: netById, columns: [{ key: 'team', label: 'Team' }, { key: 'teamPts', label: 'Team pts' }] }
    },
  },
]

export const getPlayable = (key) => PLAYABLE_GAMES.find((g) => g.key === key) || null

export function defaultOptions(key) {
  const g = getGameDef(key)
  const out = {}
  ;(g?.options || []).forEach((o) => { out[o.key] = o.default })
  return out
}
// ── SETUP-ONLY games (NEW) ──────────────────────────────────────────────────
// These 13 are set up on the web and PLAYED IN THE APP (round-trip). They are
// NOT added to PLAYABLE_GAMES (web-play is Wolf-only, test-locked) and have no
// web `settle` — GGameSetup renders them from here; createGame saves the record;
// the app's companionMap imports it and lands on the correct card. `appGameId`
// is the gameId written into the saved record's meta (what the app routes on).
// Two-axis options use the app's exact mods.betType strings so they round-trip.
export const SETUP_ONLY_GAMES = [
  // Aliases reuse the base engine in the app (meta.gameId → app routing).
  { key: 'ambrose', appGameId: 'ambrose', label: 'Ambrose / Texas Scramble', icon: '🤝', description: 'Team one ball — best shot (2/3/4 per team)', minPlayers: 2, maxPlayers: 16, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Pot', 'Hole-by-hole', 'Match'], default: 'Pot' }, { key: 'stake', label: '$ (pot / hole)', type: 'number', default: 5 } ] },
  { key: 'bestball', appGameId: 'bestball', label: 'Best Ball / 4BBB / Better Ball', icon: '🎱', description: 'Two of two — each team’s low ball counts', minPlayers: 4, maxPlayers: 4, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Pot', 'Match'], default: 'Pot' }, { key: 'stake', label: '$ (pot / hole)', type: 'number', default: 5 } ] },
  { key: 'sixes666', appGameId: 'sixes', label: '6-6-6 / Round Robin / Hollywood', icon: '🔁', description: 'Partners rotate every six holes', minPlayers: 4, maxPlayers: 4, teams: true,
    options: [ { key: 'stake', label: '$ / point', type: 'number', default: 2 } ] },
  { key: 'altshot', appGameId: 'altshot', label: 'Alternate Shot (Foursomes)', icon: '🔀', description: 'Partners alternate every shot — one team ball', minPlayers: 4, maxPlayers: 16, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Match', 'Pot'], default: 'Match' }, { key: 'stake', label: '$ (hole / pot)', type: 'number', default: 5 } ] },
  { key: 'greensomes', appGameId: 'greensomes', label: 'Greensomes', icon: '🌱', description: 'Both tee off, keep one drive, alternate in', minPlayers: 4, maxPlayers: 16, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Match', 'Pot'], default: 'Match' }, { key: 'stake', label: '$ (hole / pot)', type: 'number', default: 5 } ] },
  { key: 'pinehurst', appGameId: 'pinehurst', label: 'Pinehurst / Chapman / American Foursomes', icon: '🌲', description: 'Swap drives, pick best 2nd, alternate in', minPlayers: 4, maxPlayers: 16, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Match', 'Pot'], default: 'Match' }, { key: 'stake', label: '$ (hole / pot)', type: 'number', default: 5 } ] },
  { key: 'modstableford', appGameId: 'modstableford', label: 'Modified Stableford', icon: '🌟', description: 'Albatross 8 / Eagle 5 / Birdie 2 / Bogey −1', minPlayers: 2, maxPlayers: 10, teams: false,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Per-point', 'Pot'], default: 'Per-point' }, { key: 'stake', label: '$ / point (or pot)', type: 'number', default: 1 } ] },
  { key: 'quota', appGameId: 'quota', label: 'Quota', icon: '🎯', description: 'Beat your quota (36 − handicap)', minPlayers: 2, maxPlayers: 10, teams: false,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Pot', 'Per-point'], default: 'Pot' }, { key: 'stake', label: '$ (pot / point)', type: 'number', default: 5 } ] },
  { key: 'parbogey', appGameId: 'parbogey', label: 'Par / Bogey', icon: '🚩', description: 'Beat par +1, match 0, worse −1 — most up wins', minPlayers: 2, maxPlayers: 10, teams: false,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Pot', 'Match'], default: 'Pot' }, { key: 'stake', label: '$ (pot / match)', type: 'number', default: 5 } ] },
  { key: 'lowhigh', appGameId: 'lowhigh', label: 'Low / High Ball', icon: '⚖️', description: '2v2 — point for low ball + point for high ball', minPlayers: 4, maxPlayers: 4, teams: true,
    options: [ { key: 'lhLowTeam', label: 'Add a point for low TEAM total', type: 'bool', default: false }, { key: 'stake', label: '$ / point', type: 'number', default: 1 } ] },
  { key: 'hammer', appGameId: 'hammer', label: 'Hammer', icon: '🔨', description: 'Match play — hammer to double the hole', minPlayers: 2, maxPlayers: 4, teams: true,
    options: [ { key: 'requireAcceptance', label: 'Require acceptance (Accept / Concede)', type: 'bool', default: false }, { key: 'stake', label: '$ / hole (base)', type: 'number', default: 5 } ] },
  { key: 'best2of4', appGameId: 'best2of4', label: 'Best 2 of 4', icon: '✌️', description: 'Team of four — best two net scores each hole', minPlayers: 4, maxPlayers: 16, teams: true,
    options: [ { key: 'betType', label: 'Money', type: 'select', options: ['Pot', 'Match'], default: 'Pot' }, { key: 'stake', label: '$ (pot / hole)', type: 'number', default: 5 } ] },
  { key: 'yellowball', appGameId: 'yellowball', label: 'Yellow Ball', icon: '🟡', description: 'Team of four — rotating yellow ball must count', minPlayers: 4, maxPlayers: 16, teams: true,
    options: [ { key: 'stake', label: 'Pot buy-in $', type: 'number', default: 10 } ] },
]

// The 10 expanded junk side bets (web panel → meta.options.mods.junk; the app
// composes them zero-sum alongside Scats/Dots/Skins/CTH). auto=derived from the
// score; penalty=owes. mode is set per-bet in the panel ('pot'|'perUnit').
export const JUNK_BETS = [
  { key: 'birdie', label: 'Birdie', auto: true },
  { key: 'eagle', label: 'Eagle', auto: true },
  { key: 'sandy', label: 'Sandy' },
  { key: 'barkie', label: 'Barkie' },
  { key: 'greenie', label: 'Greenie' },
  { key: 'chippie', label: 'Chip-in / Chippie' },
  { key: 'poley', label: 'Poley' },
  { key: 'longdrive', label: 'Long Drive' },
  { key: 'threeputt', label: 'Three-putt', penalty: true },
  { key: 'water', label: 'Water / Fish', penalty: true },
]

// Combined lookup — web-playable (Wolf) OR setup-only (the 13).
export const getGameDef = (key) => getPlayable(key) || SETUP_ONLY_GAMES.find((g) => g.key === key) || null
